/**
 * Input layer. Every provider emits the same normalized InputSample:
 * { phase: "start"|"move"|"end", id, x, y, t, source, amplitude? }
 * x/y are normalized 0..1 stage coordinates (2D sources). 3D sources may also send
 * a pre-computed clinical `amplitude` + `side`, letting the MovementEngine skip 2D mapping.
 */
export class InputProvider {
  constructor() { this.listeners = new Set(); }
  get source() { return "abstract"; }
  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  emit(sample) { this.listeners.forEach((fn) => fn({ source: this.source, t: performance.now(), ...sample })); }
  attach() {}
  detach() {}
}

export class MobileTouchInput extends InputProvider {
  get source() { return "mobile_touch_simulated"; }
  attach(el) {
    this.el = el;
    const norm = (e, phase) => {
      const r = el.getBoundingClientRect();
      this.emit({ phase, id: e.pointerId, x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, aspect: r.height / r.width });
    };
    this.handlers = {
      pointerdown: (e) => { el.setPointerCapture?.(e.pointerId); norm(e, "start"); },
      pointermove: (e) => norm(e, "move"),
      pointerup: (e) => norm(e, "end"),
      pointercancel: (e) => norm(e, "end"),
    };
    Object.entries(this.handlers).forEach(([k, h]) => el.addEventListener(k, h));
  }
  detach() {
    if (!this.el) return;
    Object.entries(this.handlers).forEach(([k, h]) => this.el.removeEventListener(k, h));
    this.el = null;
  }
}

/** Placeholder: a WebXR / Unity bridge would call pushPose() with tracked controller poses. */
export class FutureVRInput extends InputProvider {
  get source() { return "vr_controller"; }
  pushPose({ id, phase, side, amplitude, x, y }) { this.emit({ id, phase, side, amplitude, x, y }); }
}

/** Placeholder: hand-tracking joints (e.g. OpenXR XR_EXT_hand_tracking) mapped to the same sample. */
export class FutureHandTrackingInput extends FutureVRInput {
  get source() { return "hand_tracking"; }
}

export const createInputProvider = (type = "mobile_touch") =>
  ({ mobile_touch: () => new MobileTouchInput(), vr: () => new FutureVRInput(), hand: () => new FutureHandTrackingInput() }[type]());
